import { z } from "zod/v4";
import { gameStatusSchema, moderationStatusSchema, skillLevelSchema } from "./shared";

export const gameSchema = z.object({
	created_at: z.coerce.date(),
	creator_id: z.string().uuid(),
	description: z.string().nullable().optional(),
	end_time: z.coerce.date().nullable().optional(),
	hidden_from_web: z.boolean().default(false),
	id: z.string().uuid(),
	latitude: z.number(),
	location_name: z.string().nullable().optional(),
	longitude: z.number(),
	// `null` = no roster cap (external venue events).
	max_players: z.number().int().nullable(),
	max_skill_level: skillLevelSchema.nullable().optional(),
	min_skill_level: skillLevelSchema.nullable().optional(),
	moderation_status: moderationStatusSchema,
	start_time: z.coerce.date().nullable().optional(),
	status: gameStatusSchema,
	title: z.string(),
	updated_at: z.coerce.date(),
});

export const accessTypeSchema = z.enum(["open", "invite_only", "tier_restricted"]);
export const gameTypeSchema = z.enum(["casual", "tournament"]);
export const gameCategorySchema = z.enum(["men", "women", "mixed"]);
export const courtKindSchema = z.enum(["free", "booked_free", "booked_paid", "not_booked"]);
export const gameDetailParticipantStatusSchema = z.enum([
	"pending",
	"confirmed",
	"waitlisted",
	"declined",
]);

export const courtArrangementSchema = z.object({
	currency: z.string().optional(),
	equipment_cents: z.number().int().optional(),
	kind: courtKindSchema,
	line_items: z.array(z.object({ cents: z.number().int(), label: z.string() })).default([]),
	total_cents: z.number().int().optional(),
});

export const gameDetailParticipantSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	display_name: z.string(),
	elo_rating: z.number().int(),
	games_hosted: z.number().int(),
	id: z.string().uuid(),
	joined_at: z.coerce.date(),
	paid_at: z.coerce.date().nullable().optional(),
	player_id: z.string().uuid(),
	skill_level: skillLevelSchema,
	status: gameDetailParticipantStatusSchema,
	team: z.number().int().nullable().optional(),
});

/** `GET /admin/games/{id}` — full detail incl. roster and court arrangement. */
export const gameDetailSchema = gameSchema.extend({
	access_type: accessTypeSchema,
	category: gameCategorySchema,
	court_arrangement: courtArrangementSchema,
	cover_image_url: z.string().nullable().optional(),
	creator_id: z.string().uuid().nullable().optional(),
	game_type: gameTypeSchema,
	is_host_anonymous: z.boolean(),
	is_public: z.boolean(),
	location_id: z.string().uuid().nullable().optional(),
	participant_count: z.number().int(),
	participants: z.array(gameDetailParticipantSchema),
});

/** Per-id result of the bulk `DELETE|PATCH /admin/games?ids=…` routes. */
export const bulkGameOutcomeSchema = z.object({
	failed: z.array(z.object({ id: z.string(), reason: z.string() })),
	succeeded: z.array(z.string()),
});
export type BulkGameOutcome = z.infer<typeof bulkGameOutcomeSchema>;
